import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	useAutomationTemplateStepDeleteMutation,
	useAutomationTemplateStepEmailOutcomeCreateMutation,
	useTemplate,
} from '../../../../queries';
import { HwcAutomationStepEditor } from '../../../components/automation/AutomationStepCard/components/HwcAutomationStepEditor';
import { styleSheet } from '../../../components/automation/AutomationStepCard/styles';
import { EditAutomationStepEmailComposerModal } from '../../../components/automation/EditAutomationStepEmailComposerModal';
import { Checkbox } from '../../../components/Checkbox';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { Modal } from '../../../components/Modal';
import { Input } from '../../../components-v2/Input';
import { HwcFrontBackPreview } from '../../../containers/PostcardComposer/HwcFrontBackPreview';
import { HwcTogglePreviewValues } from '../../../containers/PostcardComposer/HwcPreviewToggle';
import { convertHtmlValueToPostcardText } from '../../../containers/PostcardComposer/hooks/usePostcardTextEditor';
import { bs } from '../../../styles/styles';

interface HandwrittenCardStepFormProps {
	/**
	 * Current name/title of the card step.
	 */
	name: string;
	/**
	 * Selected card template ID.
	 */
	selectedTemplateId: string | null;
	/**
	 * Content editor state.
	 */
	contentEditorState: Api.IRichContentEditorState;
	/**
	 * Whether to include signature.
	 */
	includeSignature: boolean;
	/**
	 * Whether the form can be edited.
	 */
	canEdit: boolean;
	/**
	 * Whether data is being saved.
	 */
	isBusy: boolean;
	/**
	 * Unique key for the form.
	 */
	baseKey: string;
	/**
	 * The HWC automation step (needed for fallback email).
	 */
	hwcStep: Api.HandwrittenCardAutomationStep | null;
	/**
	 * Template ID for the automation template (needed for fallback email mutations).
	 */
	templateId?: string;
	/**
	 * Step ID (needed for fallback email mutations).
	 */
	stepId?: string;
	/**
	 * Impersonation context for admin operations.
	 */
	impersonationContext?: Api.IImpersonationContext;
	/**
	 * Callback when name changes.
	 */
	onNameChange: (name: string) => void;
	/**
	 * Callback when name input blurs (trigger save).
	 */
	onNameBlur: () => void;
	/**
	 * Callback to open template selector.
	 */
	onSelectTemplate: () => void;
	/**
	 * Callback when content state changes.
	 */
	onContentStateChange: (state: Api.IRichContentEditorState) => void;
	/**
	 * Callback when content editor blurs.
	 */
	onContentBlur: () => void;
	/**
	 * Callback when include signature changes.
	 */
	onIncludeSignatureChange: (include: boolean) => void;
	/**
	 * Callback to clear error messages.
	 */
	onClearError: () => void;
	/**
	 * Callback when a conditional step is added/removed, to refresh template data.
	 */
	onStepChanged?: () => void;
	/** Callback to update the step data locally after a mutation (e.g. conditional step added/removed) */
	updateStep?: (stepId: string, updater: (prev: Api.IAutomationStep) => Api.IAutomationStep) => void;
}

/**
 * Form component for editing handwritten card automation step content.
 * Displays name input, template selector, content editor, signature option,
 * and fallback email toggle.
 */
export function HandwrittenCardStepForm({
	name,
	selectedTemplateId,
	contentEditorState,
	includeSignature,
	canEdit,
	isBusy,
	baseKey,
	hwcStep,
	templateId,
	stepId,
	impersonationContext,
	onNameChange,
	onNameBlur,
	onSelectTemplate,
	onContentStateChange,
	onContentBlur,
	onIncludeSignatureChange,
	onClearError,
	onStepChanged,
	updateStep,
}: HandwrittenCardStepFormProps) {
	const isDisabled = !canEdit || isBusy;

	const userSession = useUserSession();

	// Preview card state
	const [isPreviewModalOpen, setIsPreviewModalOpen] = React.useState(false);
	const [activeTogglePreviewValue, setActiveTogglePreviewValue] = React.useState<HwcTogglePreviewValues>('text');

	// Fallback email state
	const fallbackEmailStep = hwcStep?.conditionalSteps?.[Api.AutomationStepOutcome.Error] as
		| Api.IEmailAutomationStep
		| undefined;
	const [hasFallbackEmailChecked, setHasFallbackEmailChecked] = React.useState(Boolean(fallbackEmailStep));
	const [showingEmailFallbackComposer, setShowingEmailFallbackComposer] = React.useState(false);

	// Sync fallback email state when step data changes (e.g., after template re-fetch)
	React.useEffect(() => {
		setHasFallbackEmailChecked(Boolean(hwcStep?.conditionalSteps?.[Api.AutomationStepOutcome.Error]));
	}, [hwcStep?.conditionalSteps]);

	const emailOutcomeCreateMutation = useAutomationTemplateStepEmailOutcomeCreateMutation({
		onError: () => {
			setHasFallbackEmailChecked(false);
			onClearError();
		},
		onSuccess: data => {
			// The API returns the updated parent step (with conditionalSteps populated).
			// Push it back so the UI reflects the new conditional step immediately.
			if (data && stepId) {
				updateStep?.(stepId, () => data as Api.IAutomationStep);
			}
			onStepChanged?.();
		},
	});

	const stepDeleteMutation = useAutomationTemplateStepDeleteMutation({
		onError: () => {
			setHasFallbackEmailChecked(Boolean(fallbackEmailStep));
			onClearError();
		},
		onSuccess: () => {
			// Remove the conditional step from local data
			if (stepId) {
				updateStep?.(stepId, prev => {
					const { Error: _removed, ...rest } = prev.conditionalSteps || {};
					return { ...prev, conditionalSteps: rest };
				});
			}
			onStepChanged?.();
		},
	});

	const contentEditorStateText = contentEditorState.getPlainTextPreview();
	const activeHandwritingStyle =
		hwcStep?.style ??
		userSession.user.userPreferences?.handwrittenCardPreferences?.handwritingStyle ??
		Api.HandwritingStyle.Aladdin;
	const activeSignature =
		hwcStep?.signature ?? userSession.user.userPreferences?.handwrittenCardPreferences?.signature ?? '';

	const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onNameChange(e.target.value);
		onClearError();
	};

	const handleIncludeSignatureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onIncludeSignatureChange(e.target.checked);
	};

	const handleFallbackEmailCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.checked) {
			// Remove the fallback email step
			if (fallbackEmailStep && templateId) {
				stepDeleteMutation.mutate({
					templateId,
					stepType: Api.AutomationStepType.Email,
					impersonationContext,
					stepId: fallbackEmailStep.id,
				});
			}
			setHasFallbackEmailChecked(false);
		} else {
			// Create a fallback email step with a normal blank email body.
			if (templateId && stepId) {
				emailOutcomeCreateMutation.mutate({
					impersonationContext,
					templateId,
					stepId,
					outcome: Api.AutomationStepOutcome.Error,
					step: {
						_type: 'EmailAutomationStep',
						name: 'Email Fallback Step',
						options: {},
						conditionalSchedule: {
							criteria: Api.AutomationStepScheduleCriteria.RelativeToAnchor,
							numberOfDays: 0,
							numberOfHours: 9,
							numberOfMinutes: 0,
						},
						schedule: {
							criteria: Api.AutomationStepScheduleCriteria.Conditional,
						},
					} as Api.IEmailAutomationStep,
				});
				setHasFallbackEmailChecked(true);
			}
		}
	};

	const handlePreviewOpen = () => {
		setActiveTogglePreviewValue('text');
		setIsPreviewModalOpen(true);
	};

	const isFallbackBusy = emailOutcomeCreateMutation.isPending || stepDeleteMutation.isPending;

	const isFallbackDisabled =
		isDisabled || (!fallbackEmailStep && (!contentEditorStateText || !selectedTemplateId)) || isFallbackBusy;

	return (
		<div className={css(bs.flex, bs.flexCol, bs.gap3, bs.wFull, bs.mt4)}>
			{/* Card title */}
			<div className={css(styleSheet.fieldLabel)}>
				Card title: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<Input
				autoComplete='off'
				styles={[styleSheet.emailNameInput]}
				disabled={isDisabled}
				id={`automation-hwc-step-name-input-${baseKey}`}
				key={`${baseKey}-name-input`}
				onBlur={onNameBlur}
				onChange={handleNameChange}
				onFocus={onClearError}
				placeholder='Enter a brief description of this card'
				type='text'
				value={name || ''}
			/>

			{/* Template selector */}
			<div className={css(bs.flex, bs.gap3, bs.itemsStart)}>
				<HwcChooseCardButton templateId={selectedTemplateId} onClick={onSelectTemplate} disabled={isDisabled} />
			</div>

			{/* Content editor */}
			<div className={css(bs.flex, bs.flexCol, bs.gap1, bs.wFull)}>
				<span className={css(styleSheet.fieldLabel)}>
					Card content: &nbsp;
					<span className={css(bs.required)}>*</span>
				</span>
				<HwcAutomationStepEditor
					contentState={contentEditorState}
					onBlur={onContentBlur}
					onContentStateChanged={onContentStateChange}
				/>
			</div>

			{/* Include signature checkbox */}
			<Checkbox
				id={`automation-hwc-step-signature-checkbox-${baseKey}`}
				checked={includeSignature}
				onChange={handleIncludeSignatureChange}
				disabled={isDisabled}
			>
				<span>Include signature of sender</span>
			</Checkbox>

			{/* Preview Card button - only shown when template and content are present */}
			{selectedTemplateId && contentEditorState.getPlainTextPreview() ? (
				<button className={css(bs.ctaButton)} onClick={handlePreviewOpen}>
					Preview Card
				</button>
			) : null}

			{/* Preview Card modal */}
			<Modal isOpen={isPreviewModalOpen} onRequestClose={() => setIsPreviewModalOpen(false)} useDefaultHeader>
				{isPreviewModalOpen && selectedTemplateId ? (
					<div className={css(bs.p4)}>
						<HwcFrontBackPreview
							templateId={selectedTemplateId}
							value={activeTogglePreviewValue}
							handwritingStyle={activeHandwritingStyle}
							signature={includeSignature ? activeSignature : ''}
							onValueChange={setActiveTogglePreviewValue}
							postcardText={convertHtmlValueToPostcardText(contentEditorState.getRawRichTextContent().document)}
						/>
					</div>
				) : null}
			</Modal>

			{/* Fallback email checkbox */}
			<div className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
				<Checkbox
					checked={hasFallbackEmailChecked}
					onChange={handleFallbackEmailCheckChange}
					disabled={isFallbackDisabled}
				>
					<span>If can&apos;t send card, send email instead</span>
				</Checkbox>
				{isFallbackBusy && <LoadingSpinner type='extra-small' />}
			</div>

			{/* Fallback email composer */}
			{fallbackEmailStep && (
				<div className={css(bs.flex, bs.flexCol, bs.textLabel, bs.textSm, bs.wFull)}>
					<div>Enter backup email content:</div>
					<button
						className={css(
							bs.flex,
							bs.itemsCenter,
							bs.textSm,
							bs.justifyCenter,
							bs.wFull,
							bs.h15,
							bs.textBrandPrimary,
							!fallbackEmailStep?.options?.subject ? styleSheet.emailCreateButton : styleSheet.emailEditButton
						)}
						disabled={!fallbackEmailStep || stepDeleteMutation.isPending}
						onClick={() => setShowingEmailFallbackComposer(true)}
					>
						<span>{`${fallbackEmailStep?.options?.subject ? 'Preview / Edit' : 'Create'} Email`}</span>
					</button>
				</div>
			)}
			{showingEmailFallbackComposer && fallbackEmailStep && templateId && (
				<EditAutomationStepEmailComposerModal
					automationTemplateId={templateId}
					step={fallbackEmailStep}
					onAttachmentRemoved={() => {
						onStepChanged?.();
					}}
					readonly={!canEdit}
					syncSubjectWithStep
					onRequestClose={() => {
						setShowingEmailFallbackComposer(false);
						onStepChanged?.();
					}}
					onStepChanged={onStepChanged}
					impersonationContext={impersonationContext}
				/>
			)}
		</div>
	);
}

/**
 * Card template preview button / selector.
 */
function HwcChooseCardButton({
	templateId,
	onClick,
	disabled,
}: {
	templateId: string | null;
	onClick: () => void;
	disabled: boolean;
}) {
	const templateQuery = useTemplate<Api.IHandwrittenCardTemplate>({
		templateId: templateId ?? undefined,
		enabled: Boolean(templateId),
	});

	if (templateId) {
		if (templateQuery.isSuccess) {
			return (
				<div
					className={css(bs.h28, bs.w44, bs.rounded, bs.cursorPointer, disabled && bs.opacity40)}
					onClick={disabled ? undefined : onClick}
				>
					<img
						src={templateQuery.data.image.publicUrl}
						className={css(bs.block, bs.hFull, bs.wFull, bs.objectCover)}
						alt='Card template preview'
					/>
				</div>
			);
		}
		return (
			<div className={css(bs.h28, bs.w44, bs.flex, bs.itemsCenter, bs.justifyCenter)} onClick={onClick}>
				<LoadingSpinner type='small' />
			</div>
		);
	}

	return (
		<div
			className={css(
				bs.border,
				bs.borderDashed,
				bs.borderAlternateTitle,
				bs.rounded,
				bs.h28,
				bs.w44,
				bs.textAlternateTitle,
				bs.flex,
				bs.itemsCenter,
				bs.justifyCenter,
				bs.cursorPointer,
				disabled && bs.opacity40
			)}
			onClick={disabled ? undefined : onClick}
		>
			<span>Choose Card</span>
		</div>
	);
}
